import i18nFactory from '@yandex-int/i18n';
import { isUndefined } from 'lodash';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { EMPTY, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { getType } from 'typesafe-actions';

import { confirm } from '../../service/Confirm/Confirm';
import { CanvasPcodeVideoFrameParams } from '../../service/Pcode/PcodeCanvasVideo';
import { EpicDeps } from '../../service/StoreFactory';
import { State } from '../../typings/state';
import { applyGraphicCreativeFile, changeOptionEditable, changeOptionsOverlayColor } from '../AddCreativePage/actions';
import { getVideoCreativeState } from '../AddCreativePage/AddVideoCreative/helpers';
import { getIsCpmAudio } from '../AddCreativePage/AddVideoCreative/helpers';
import { CanvasAction } from '../common/actions';
import { getValidationErrorData, isGraphicCreatives, isVideoAddition } from '../common/helpers';
import { DEFAULT_GALLERY_FILES_REQUEST, ResourceType } from '../common/typings';
import { VideoOptionName } from '../CreativeOptions/typings';
import { resetAudioFileId, restoreAudioFileId, setFile, sourceChange } from '../FileEditor/actions';
import { selectFileEditorState } from '../FileEditor/selectors';
import { FileEditorOptions } from '../FileEditor/typings';

import {
    apiActionError,
    applyItem,
    ApplyItem,
    categoriesDataReceived,
    changeEnabledGalleryTypes,
    changeRecentNameFilter,
    changeRecentOrder,
    changeSelectedGalleryCategories,
    changeSemanticTextFilter,
    closeGalleryPopup,
    editItemName,
    EditItemName,
    fetchGalleryCategoriesData,
    FetchGalleryFiles,
    fetchGalleryFiles,
    FetchInitialData,
    fetchInitialData,
    fetchItemsSuccess,
    GalleryAction,
    loadMoreItems,
    loadMoreItemsSuccess,
    OpenGalleryPopup,
    openGalleryPopup,
    previewError,
    PreviewVideoItem,
    previewVideoItem,
    ReloadRecentCollection,
    reloadRecentCollection,
    removeGalleryItemSuccess,
    removeItem,
    RemoveItem,
    videoPreviewDataReceived
} from './actions';
// tslint:disable-next-line:no-external-i18n-import
import * as keyset from './GalleryPane.i18n';
import {
    galleryTypesNeedToFetchOnOpen,
    getAutoLoadGalleryTypes,
    getCreativeOptionsWithVideoGalleryItem,
    getGalleryTypesForResource,
    getRequestFiltersFromGalleryFilters,
    hasPaneTypeSources,
    needToChangeOverlayColor,
    needToReloadRecent,
    resetGalleryFetchParams
} from './helpers';
import {
    selectCurrentGallery,
    selectCurrentGalleryPaneType,
    selectGalleryPane,
    selectRecentGalleryPane,
    selectStockGalleryPane
} from './selectors';
import { FetchGalleryCategoriesResponse, GalleryPaneTypes } from './typings';

const DEBOUNCE_TIMEOUT = 1000;
const i18n = i18nFactory(keyset);

const showApiActionError = (message: string) => {
    return of(apiActionError(message));
};

export const disableAudioOption = (options: FileEditorOptions): CanvasAction[] => [
    changeOptionEditable(options.groupName, VideoOptionName.audio, false),
    resetAudioFileId()
];

export const enableAudioOption = (options: FileEditorOptions): CanvasAction[] => [
    changeOptionEditable(options.groupName, VideoOptionName.audio, true),
    restoreAudioFileId()
];

const changeFilterEpic: Epic<GalleryAction, GalleryAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(changeRecentNameFilter), getType(changeSemanticTextFilter)),
        debounceTime(DEBOUNCE_TIMEOUT),
        map(() => {
            const galleries = store$.value.canvasEditor.canvas.galleries;
            const { currentResourceType } = galleries;
            const currentPaneType = selectCurrentGalleryPaneType(galleries);

            const requestData = DEFAULT_GALLERY_FILES_REQUEST;

            if (currentPaneType === GalleryPaneTypes.stock) {
                const { filters } = selectStockGalleryPane(galleries);

                requestData.semantic_search = filters.semanticText;
            }

            if (currentPaneType === GalleryPaneTypes.recent) {
                const { filters } = selectRecentGalleryPane(galleries);

                requestData.name = filters.recentNameFilter;
                requestData.sort_order = filters.recentOrder;
            }

            return fetchGalleryFiles(currentResourceType, currentPaneType, requestData);
        })
    );
};

const changeOrderFilterEpic: Epic<GalleryAction, GalleryAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(changeRecentOrder)),
        filter(() => {
            const galleries = store$.value.canvasEditor.canvas.galleries;
            const pane = selectGalleryPane(galleries);

            return pane.data.collection.length > 1;
        }),
        map(() => {
            const galleries = store$.value.canvasEditor.canvas.galleries;
            const { currentResourceType } = galleries;

            const requestData = DEFAULT_GALLERY_FILES_REQUEST;
            const { filters } = selectRecentGalleryPane(galleries);

            requestData.name = filters.recentNameFilter;
            requestData.sort_order = filters.recentOrder;

            return fetchGalleryFiles(currentResourceType, GalleryPaneTypes.recent, requestData);
        })
    );
};

const changeCategoriesFilterEpic: Epic<GalleryAction, GalleryAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(changeSelectedGalleryCategories)),
        debounceTime(DEBOUNCE_TIMEOUT),
        map(() => {
            const { galleries } = store$.value.canvasEditor.canvas;
            const { currentResourceType } = galleries;

            // фильтр по категориям есть только на панели GalleryPaneTypes.stock
            const currentPaneType = GalleryPaneTypes.stock;
            const { filters } = selectStockGalleryPane(galleries);

            // мы выставляем по умолчанию, потому что хотим сбросить значение offset
            const requestData = DEFAULT_GALLERY_FILES_REQUEST;

            if (currentResourceType === ResourceType.image) {
                requestData.categories = filters.selectedGalleryCategories;
            }

            if (currentResourceType === ResourceType.video) {
                requestData.groups = filters.selectedGalleryCategories;
            }

            return fetchGalleryFiles(currentResourceType, currentPaneType, requestData);
        })
    );
};

const loadMoreFilterEpic: Epic<GalleryAction, GalleryAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(loadMoreItems)),
        debounceTime(DEBOUNCE_TIMEOUT),
        switchMap(() => {
            const {
                canvas: {
                    galleries,
                    commonData: { ideaId }
                },
                sharedData
            } = store$.value.canvasEditor;
            const { currentResourceType } = galleries;
            const currentPaneType = selectCurrentGalleryPaneType(galleries);
            const isVideo = isVideoAddition(sharedData.creativeType);
            const { filters } = selectGalleryPane(galleries);

            const requestData = getRequestFiltersFromGalleryFilters(
                currentPaneType,
                filters,
                currentResourceType === ResourceType.video
            );

            return from(
                deps.canvasApi.galleries.fetchGalleryFiles(
                    currentResourceType,
                    currentPaneType,
                    {
                        ...requestData,
                        offset: requestData.offset + requestData.limit
                    },
                    sharedData,
                    isVideo,
                    ideaId
                )
            ).pipe(
                map(response => loadMoreItemsSuccess(currentResourceType, currentPaneType, response)),
                catchError(error => showApiActionError(error.message || error.getNativeError().message))
            );
        })
    );
};

const fetchGalleryCategoriesEpic: Epic<GalleryAction, GalleryAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(fetchGalleryCategoriesData)),
        switchMap(() => {
            const {
                canvas: { galleries },
                sharedData
            } = store$.value.canvasEditor;
            const { currentResourceType } = galleries;
            const currentPaneType = selectCurrentGalleryPaneType(galleries);

            return from(deps.canvasApi.galleries.fetchGalleryCategories(currentResourceType, sharedData)).pipe(
                map((fetchResult: FetchGalleryCategoriesResponse) =>
                    categoriesDataReceived({
                        rawData: fetchResult.items,
                        galleryType: currentPaneType
                    })
                ),
                catchError(error => showApiActionError(error.message || error.getNativeError().message))
            );
        })
    );
};

const showPreviewError = (message: string = i18n('preview-error')) => {
    return of(previewError(getValidationErrorData(message)));
};

const showVideoPreviewEpic: Epic<GalleryAction, GalleryAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(previewVideoItem)),
        switchMap((action: PreviewVideoItem) => {
            if (isUndefined(action.params)) {
                return EMPTY;
            }

            const {
                sharedData,
                canvas: { addCreative, fileEditors, galleries }
            } = store$.value.canvasEditor;

            const addVideoCreative = getVideoCreativeState(addCreative);
            if (!addVideoCreative) {
                return showPreviewError();
            }

            const { creativeOptions, presetId, videoPreviewData, bundle } = addVideoCreative;

            const { fileEditorId, panes } = selectCurrentGallery(galleries);
            const galleryItem = panes[action.paneType].data.collection[action.params.itemIndex];

            if (!creativeOptions || !presetId || !videoPreviewData || !bundle || !fileEditorId) {
                return showPreviewError();
            }

            const { options } = selectFileEditorState(fileEditors, fileEditorId);

            return from(
                deps.canvasApi.addVideoCreative.fetchPreviewVast(
                    getCreativeOptionsWithVideoGalleryItem(creativeOptions.optionsValue, galleryItem.id, options),
                    bundle,
                    presetId,
                    videoPreviewData,
                    sharedData
                )
            ).pipe(
                map((params: Partial<CanvasPcodeVideoFrameParams>) => videoPreviewDataReceived(params)),
                catchError(error => showPreviewError(error.message || error.getNativeError().message))
            );
        })
    );
};

const editGalleryItemNameEpic: Epic<GalleryAction, GalleryAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(editItemName)),
        switchMap((action: EditItemName) => {
            const {
                sharedData,
                canvas: { galleries }
            } = store$.value.canvasEditor;
            const { data } = selectGalleryPane(galleries);

            const isVideo = isVideoAddition(sharedData.creativeType);
            const galleryItem = data.collection[action.itemCollectionIndex];

            return from(
                deps.canvasApi.galleries.editGalleryItem(
                    galleryItem.id,
                    {
                        name: galleryItem.name
                    },
                    sharedData,
                    isVideo
                )
            ).pipe(
                switchMap(() => EMPTY),
                catchError(error => showApiActionError(error.message || error.getNativeError().message))
            );
        })
    );
};

const removeGalleryItemEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(removeItem)),
        switchMap((action: RemoveItem) => {
            const {
                sharedData,
                canvas: { galleries }
            } = store$.value.canvasEditor;
            const { currentResourceType } = galleries;
            const { data, filters } = selectRecentGalleryPane(galleries);

            const isVideo = isVideoAddition(sharedData.creativeType);
            const galleryItem = data.collection[action.itemCollectionIndex];

            return from(
                confirm({
                    message: i18n('remove-item-confirmation', {
                        name: action.itemName
                    })
                })
            ).pipe(
                switchMap(() =>
                    from(deps.canvasApi.galleries.removeGalleryItem(galleryItem.id, isVideo, sharedData)).pipe(
                        switchMap(() => {
                            const hasRecentItems = data.total !== 1 || Boolean(filters.recentNameFilter);

                            return [
                                sourceChange(currentResourceType, {
                                    [GalleryPaneTypes.recent]: hasRecentItems
                                }),
                                removeGalleryItemSuccess(
                                    currentResourceType,
                                    GalleryPaneTypes.recent,
                                    action.itemCollectionIndex
                                )
                            ];
                        })
                    )
                ),
                catchError(error => showApiActionError(error.message || error.getNativeError().message))
            );
        })
    );
};

export const applyVideoGalleryItemEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(applyItem)),
        filter(() => isVideoAddition(store$.value.canvasEditor.sharedData.creativeType)),
        switchMap((action: ApplyItem): Observable<CanvasAction> | CanvasAction[] => {
            const {
                sharedData,
                canvas: {
                    fileEditors,
                    galleries,
                    commonData: { ideaId }
                }
            } = store$.value.canvasEditor;
            const { currentResourceType } = galleries;
            const { currentPaneType, fileEditorId } = selectCurrentGallery(galleries);
            const {
                data: { collection }
            } = selectGalleryPane(galleries);

            const isVideo = isVideoAddition(sharedData.creativeType);
            const galleryItem = collection[action.itemCollectionIndex];
            const close$: CanvasAction[] = [closeGalleryPopup()];

            if (!fileEditorId) {
                return close$;
            }

            const editor = selectFileEditorState(fileEditors, fileEditorId);
            const needToChangeAudio = !getIsCpmAudio(sharedData) && galleryItem.type === ResourceType.video;

            if (galleryItem.type === ResourceType.video && needToChangeOverlayColor(galleryItem, sharedData)) {
                close$.push(changeOptionsOverlayColor(galleryItem.overlayColor));
            }

            if (currentPaneType === GalleryPaneTypes.recent) {
                // У пользовательских видеофайлов нужно отключать аудио CANVAS-472
                if (editor.options && needToChangeAudio) {
                    if (galleryItem.stock_file_id) {
                        close$.push(...enableAudioOption(editor.options));
                    } else {
                        close$.push(...disableAudioOption(editor.options));
                    }
                }

                close$.push(setFile(fileEditorId, galleryItem));

                return close$;
            }

            return from(
                deps.canvasApi.galleries.applyExternalGalleryItem(
                    currentResourceType,
                    currentPaneType,
                    galleryItem.id,
                    ideaId,
                    isVideo,
                    sharedData
                )
            ).pipe(
                switchMap(response => {
                    if (editor.options && needToChangeAudio) {
                        close$.push(...enableAudioOption(editor.options));
                    }

                    close$.push(setFile(fileEditorId, response), reloadRecentCollection(currentResourceType));

                    return close$;
                }),
                catchError(error => showApiActionError(error.message || error.getNativeError().message))
            );
        })
    );
};

const applyGalleryItemEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(applyItem)),
        filter(() => isGraphicCreatives(store$.value.canvasEditor.sharedData.creativeType)),
        switchMap((action: ApplyItem): Observable<CanvasAction> | CanvasAction[] => {
            const {
                sharedData,
                canvas: {
                    galleries,
                    fileEditors,
                    commonData: { ideaId }
                }
            } = store$.value.canvasEditor;
            const { currentResourceType } = galleries;
            const { currentPaneType, fileEditorId } = selectCurrentGallery(galleries);
            const {
                data: { collection }
            } = selectGalleryPane(galleries);

            const galleryItem = collection[action.itemCollectionIndex];

            if (!fileEditorId) {
                return of(closeGalleryPopup());
            }

            const editor = selectFileEditorState(fileEditors, fileEditorId);

            if (!editor.options) {
                return of(closeGalleryPopup());
            }

            const groupName = editor.options.groupName;

            if (currentPaneType === GalleryPaneTypes.recent) {
                return [closeGalleryPopup(), applyGraphicCreativeFile(fileEditorId, galleryItem, groupName)];
            }

            return from(
                deps.canvasApi.galleries.applyExternalGalleryItem(
                    currentResourceType,
                    currentPaneType,
                    galleryItem.id,
                    ideaId,
                    false,
                    sharedData
                )
            ).pipe(
                switchMap(response => {
                    return [
                        closeGalleryPopup(),
                        applyGraphicCreativeFile(fileEditorId, response, groupName),
                        reloadRecentCollection(currentResourceType)
                    ];
                }),
                catchError(error => showApiActionError(error.message || error.getNativeError().message))
            );
        })
    );
};

const openGalleryPopupEpic: Epic<GalleryAction, GalleryAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(openGalleryPopup)),
        filter((action: OpenGalleryPopup) => {
            const { resourceType, paneType } = action;
            const gallery = store$.value.canvasEditor.canvas.galleries.resources[resourceType];

            return !gallery.panes[paneType].isFetched || Boolean(galleryTypesNeedToFetchOnOpen(gallery).length);
        }),
        switchMap((action: OpenGalleryPopup) => {
            const { resourceType } = action;
            const gallery = store$.value.canvasEditor.canvas.galleries.resources[resourceType];
            const galleriesNeedToFetch = galleryTypesNeedToFetchOnOpen(gallery);

            return galleriesNeedToFetch.map(type =>
                fetchGalleryFiles(resourceType, type, DEFAULT_GALLERY_FILES_REQUEST)
            );
        })
    );
};

const fetchGalleryFilesEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(fetchGalleryFiles)),
        mergeMap((action: FetchGalleryFiles) => {
            const { resourceType, paneType, requestData } = action;
            const {
                sharedData,
                canvas: {
                    commonData: { ideaId }
                }
            } = store$.value.canvasEditor;
            const isVideo = isVideoAddition(sharedData.creativeType);

            return from(
                deps.canvasApi.galleries.fetchGalleryFiles(
                    resourceType,
                    paneType,
                    requestData,
                    sharedData,
                    isVideo,
                    ideaId
                )
            ).pipe(
                switchMap(response => {
                    const result$: CanvasAction[] = [fetchItemsSuccess(resourceType, paneType, response)];

                    if (paneType === GalleryPaneTypes.recent) {
                        const hasRecentItems = Boolean(response.total) || Boolean(requestData.name);
                        result$.push(
                            sourceChange(resourceType, {
                                [GalleryPaneTypes.recent]: hasRecentItems
                            })
                        );
                    }

                    return result$;
                }),
                catchError(error => showApiActionError(error.message || error.getNativeError().message))
            );
        })
    );
};

const reloadRecentCollectionEpic: Epic<GalleryAction, GalleryAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(reloadRecentCollection)),
        filter((action: ReloadRecentCollection) => {
            const {
                sharedData,
                canvas: {
                    commonData: { ideaId }
                }
            } = store$.value.canvasEditor;

            return needToReloadRecent(action.resourceType, sharedData, Boolean(ideaId));
        }),
        map((action: ReloadRecentCollection) => {
            const { resourceType } = action;
            const { galleries } = store$.value.canvasEditor.canvas;
            const filters = resetGalleryFetchParams(selectRecentGalleryPane(galleries).filters);

            const requestData = getRequestFiltersFromGalleryFilters(
                GalleryPaneTypes.recent,
                filters,
                resourceType === ResourceType.video
            );

            return fetchGalleryFiles(resourceType, GalleryPaneTypes.recent, requestData);
        })
    );
};

export const fetchInitialGalleryDataEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(getType(fetchInitialData)),
        mergeMap((action: FetchInitialData) => {
            const { resourceType } = action;
            const {
                sharedData,
                canvas: {
                    commonData: { ideaId }
                }
            } = store$.value.canvasEditor;
            const enabledGalleryTypes = getGalleryTypesForResource(resourceType, sharedData, Boolean(ideaId));
            const autoLoadGalleries = getAutoLoadGalleryTypes(enabledGalleryTypes);
            const isVideo = isVideoAddition(sharedData.creativeType);

            if (!autoLoadGalleries.length) {
                return EMPTY;
            }

            return forkJoin(
                autoLoadGalleries.map(galleryType =>
                    from(
                        deps.canvasApi.galleries.fetchGalleryFiles(
                            resourceType,
                            galleryType,
                            DEFAULT_GALLERY_FILES_REQUEST,
                            sharedData,
                            isVideo,
                            ideaId
                        )
                    )
                )
            ).pipe(
                switchMap(responses => {
                    const hasPaneSources: Partial<Record<GalleryPaneTypes, boolean>> = {};
                    const setGalleriesData$ = autoLoadGalleries.map((paneType, index) => {
                        const response = responses[index];

                        hasPaneSources[paneType] = hasPaneTypeSources(
                            enabledGalleryTypes,
                            response.collection,
                            paneType
                        );

                        return fetchItemsSuccess(resourceType, paneType, response);
                    });

                    return [
                        changeEnabledGalleryTypes(resourceType, enabledGalleryTypes),
                        ...setGalleriesData$,
                        sourceChange(resourceType, hasPaneSources)
                    ];
                }),
                catchError(error => showApiActionError(error.message || error.getNativeError().message))
            );
        })
    );
};

export const galleryEpics = combineEpics(
    changeFilterEpic,
    changeCategoriesFilterEpic,
    fetchGalleryCategoriesEpic,
    editGalleryItemNameEpic,
    loadMoreFilterEpic,
    applyGalleryItemEpic,
    applyVideoGalleryItemEpic,
    removeGalleryItemEpic,
    showVideoPreviewEpic,
    openGalleryPopupEpic,
    fetchGalleryFilesEpic,
    fetchInitialGalleryDataEpic,
    reloadRecentCollectionEpic,
    changeOrderFilterEpic
);
